import React, { useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import {
  FavoriteBorder,
  LocalFireDepartmentOutlined,
  CloudOutlined,
  CelebrationOutlined,
  MoodOutlined,
  TerrainOutlined,
  DarkModeOutlined,
  BoltOutlined,
  ExpandMore,
  Check as CheckIcon,
} from '@mui/icons-material';
import CardQuickViewModal from '../CardQuickViewModal';
import CardCoverImage from '../CardCoverImage';
import { FEELING_FAMILIES } from '../../lib/feelingTaxonomy';
import { samplesForFeeling } from '../../lib/feelingSamples';

const TEAL = '#206884';

const FAMILY_ICON = {
  Tender: FavoriteBorder,
  Reverent: LocalFireDepartmentOutlined,
  Unfiltered: CloudOutlined,
  Joyful: CelebrationOutlined,
  Playful: MoodOutlined,
  Bold: TerrainOutlined,
  Dreamy: DarkModeOutlined,
  Striking: BoltOutlined,
};

// Every family starts COLLAPSED on load (approved mockup store-quickview-looping.html) — the shopper
// opens the families that interest them; nothing is pre-expanded.
const DEFAULT_OPEN = new Set();

// Home-card size tokens — a sample thumbnail is IDENTICAL in footprint to a home browse tile.
const THUMB_WIDTH = { xs: '28vw', sm: 150, md: 160 };
const THUMB_MAX_WIDTH = { xs: 150, sm: 160, md: 180 };

// A bare, style-reset <button> — no MUI ButtonBase (its ripple sets up timers that slow the wizard
// tests and leak act() warnings). Plain buttons keep the accordion cheap to render + fully accessible.
const resetBtn = {
  m: 0,
  border: 0,
  background: 'transparent',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
};

/**
 * Feeling step — the approved accordion wizard (docs/mockups/store-quickview-looping.html). 8 feeling
 * FAMILIES (collapsible; ALL closed on load), each feeling = a FLAT row (no per-feeling box; rows are
 * separated by a hairline divider, so the family accordion is the only container) with name + blurb +
 * a Select pill + a horizontally-scrollable rail of HOME-SIZED example cards. Tapping a thumbnail
 * opens the shared quick-view lightbox, which loops through that feeling's samples (with an honest
 * "i / total" cue) and offers "Use this feeling".
 *
 * Multi-select across families → the parent submits `feeling: string[]` (names), exactly as before.
 */
const FeelingStep = ({ selected = [], onToggle, ownFeeling = '', onOwnFeeling }) => {
  const [open, setOpen] = useState(DEFAULT_OPEN);
  // Lightbox state: the feeling being previewed + which sample index is showing (looping gallery).
  const [preview, setPreview] = useState(null); // { name, index }

  const isOpen = (fam) => open.has(fam);
  const toggleFamily = (fam) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(fam)) next.delete(fam);
      else next.add(fam);
      return next;
    });

  const isSelected = (name) => selected.includes(name);
  const total = selected.length;

  const openPreview = (name, index) => setPreview({ name, index });
  const closePreview = () => setPreview(null);
  const confirmPreview = () => {
    if (preview && !isSelected(preview.name)) onToggle(preview.name);
    closePreview();
  };

  // The previewed feeling's samples as looping gallery items (empty feeling → placeholder, no arrows).
  const previewSamples = preview ? samplesForFeeling(preview.name) : [];
  const previewItems = previewSamples.map((url, i) => ({
    coverImageUrl: url,
    alt: `${preview ? preview.name : ''} example ${i + 1}`,
  }));
  const previewIndex = preview ? preview.index : 0;

  return (
    <Box>
      <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
        What feeling fits?
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        What feeling are you going for? Open a family, see a few examples, and pick whatever fits — you
        can choose more than one.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {FEELING_FAMILIES.map((fam) => {
          const FamIcon = FAMILY_ICON[fam.name] || FavoriteBorder;
          const famCount = fam.feels.filter((f) => isSelected(f.name)).length;
          const expanded = isOpen(fam.name);
          return (
            <Box
              key={fam.name}
              sx={{ border: '1px solid #e7e3d6', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => toggleFamily(fam.name)}
                aria-expanded={expanded}
                aria-label={`${fam.name} — ${fam.vibe}`}
                sx={{
                  ...resetBtn,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: '12px 14px',
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${fam.color}14`,
                    flexShrink: 0,
                  }}
                >
                  <FamIcon sx={{ color: fam.color, fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>{fam.name}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{fam.vibe}</Typography>
                </Box>
                {famCount > 0 && (
                  <Box
                    data-testid={`feeling-badge-${fam.name}`}
                    sx={{
                      minWidth: 22,
                      height: 22,
                      px: 0.75,
                      borderRadius: '11px',
                      backgroundColor: TEAL,
                      color: '#fff',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {famCount}
                  </Box>
                )}
                <ExpandMore
                  sx={{ color: '#b3bbb6', transform: expanded ? 'rotate(180deg)' : 'none' }}
                />
              </Box>

              {expanded && (
                // Trimmed horizontal padding so the flattened rows use the full accordion width.
                <Box sx={{ p: '0 12px 8px' }}>
                  {fam.feels.map((feel, feelIdx) => {
                    const sel = isSelected(feel.name);
                    const samples = samplesForFeeling(feel.name);
                    return (
                      // FLAT row: no border/background/rounded box — a single hairline divider between
                      // feelings (none above the first). The family accordion is the only container.
                      <Box
                        key={feel.name}
                        sx={{
                          pt: feelIdx === 0 ? '8px' : '14px',
                          pb: '14px',
                          px: '2px',
                          borderTop: feelIdx === 0 ? 'none' : '1px solid #efe9da',
                        }}
                      >
                        {/* Whole row toggles selection. Selected state is shown WITHOUT a box: a filled
                            teal Selected pill + the feeling name in teal. */}
                        <Box
                          component="button"
                          type="button"
                          onClick={() => onToggle(feel.name)}
                          aria-label={`${sel ? 'Deselect' : 'Select'} ${feel.name}`}
                          aria-pressed={sel}
                          sx={{ ...resetBtn, display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: sel ? TEAL : 'inherit' }}>
                              {feel.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                              {feel.blurb}
                            </Typography>
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              flexShrink: 0,
                              borderRadius: '8px',
                              px: 1.25,
                              py: 0.5,
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              border: `1px solid ${TEAL}`,
                              color: sel ? '#fff' : TEAL,
                              backgroundColor: sel ? TEAL : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sel && <CheckIcon sx={{ fontSize: 16 }} />}
                            {sel ? 'Selected' : 'Select'}
                          </Box>
                        </Box>

                        {/* Home-sized example cards in a horizontally-scrollable rail (no hint text) —
                            or a calm same-sized placeholder when a feeling has no samples yet. */}
                        <Box
                          data-testid={`feeling-thumbs-${feel.name}`}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            mt: 1.5,
                            overflowX: 'auto',
                            pb: 0.75,
                            scrollSnapType: 'x mandatory',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                          }}
                        >
                          {samples.length > 0 ? (
                            samples.map((url, i) => (
                              <Box
                                key={url + i}
                                component="button"
                                type="button"
                                onClick={() => openPreview(feel.name, i)}
                                aria-label={`Preview ${feel.name} example ${i + 1}`}
                                data-testid={`feeling-thumb-${feel.name}`}
                                sx={{
                                  ...resetBtn,
                                  p: 0,
                                  flex: '0 0 auto',
                                  width: THUMB_WIDTH,
                                  maxWidth: THUMB_MAX_WIDTH,
                                  scrollSnapAlign: 'start',
                                }}
                              >
                                <CardCoverImage
                                  coverImageUrl={url}
                                  alt={`${feel.name} example ${i + 1}`}
                                  sx={{
                                    width: '100%',
                                    aspectRatio: '5 / 7',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  }}
                                />
                              </Box>
                            ))
                          ) : (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => openPreview(feel.name, 0)}
                              aria-label={`Preview ${feel.name}`}
                              data-testid={`feeling-placeholder-${feel.name}`}
                              sx={{
                                ...resetBtn,
                                flex: '0 0 auto',
                                width: THUMB_WIDTH,
                                maxWidth: THUMB_MAX_WIDTH,
                                aspectRatio: '5 / 7',
                                borderRadius: '8px',
                                border: '1px dashed #d8d2c2',
                                backgroundColor: '#f6f3ea',
                                color: '#b3bbb6',
                                fontSize: '0.72rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                p: 0.5,
                                scrollSnapAlign: 'start',
                              }}
                            >
                              Sample soon
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography data-testid="feeling-total" sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
        {total === 0 ? 'No feelings selected yet' : `${total} feeling${total === 1 ? '' : 's'} selected`}
      </Typography>

      <TextField
        fullWidth
        label="Or say it in your own words"
        value={ownFeeling}
        onChange={(e) => onOwnFeeling(e.target.value)}
        sx={{ mt: 2 }}
        placeholder="e.g. proud, bittersweet, a little cheeky…"
      />

      <CardQuickViewModal
        open={!!preview}
        onClose={closePreview}
        items={previewItems.length ? previewItems : undefined}
        index={previewIndex}
        onIndexChange={(next) => setPreview((p) => (p ? { ...p, index: next } : p))}
        count={previewItems.length > 1 ? { index: previewIndex, total: previewItems.length } : undefined}
        alt={preview ? `${preview.name} example` : ''}
        actionLabel="Use this feeling"
        onConfirm={confirmPreview}
        ariaLabel={preview ? `${preview.name} example preview` : 'Feeling example preview'}
        testId="feeling-lightbox"
      />
    </Box>
  );
};

export default FeelingStep;
